import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowDownLeftIcon,
  ArrowUpRightIcon,
  HistoryIcon,
  ReceiptIcon,
} from 'lucide-react';

import { Badge } from '@/components/badge/badge';
import { Button } from '@/components/button/button';
import { Skeleton } from '@/components/skeleton/skeleton';
import { getMentorBalanceHistoryQueryOptions } from '@/features/users/api/get-mentor-balance-history.api';
import type { MentorBalanceHistoryEntry } from '@/features/users/types/mentor-balance-history.type';
import { formatRupiah } from '@/features/users/utils/format-rupiah';
import { formatDate } from '@/utils/date';

const LIMIT = 10;

export interface MentorBalanceHistoryProps {
  idBalance: number;
}

export default function MentorBalanceHistory({
  idBalance,
}: MentorBalanceHistoryProps) {
  return (
    <MentorBalanceHistorySection>
      <MentorBalanceHistoryContent idBalance={idBalance} />
    </MentorBalanceHistorySection>
  );
}

function MentorBalanceHistoryContent({ idBalance }: MentorBalanceHistoryProps) {
  const [page, setPage] = useState(1);

  const getMentorBalanceHistoryQuery = useQuery(
    getMentorBalanceHistoryQueryOptions({
      id_balance: idBalance,
      page,
      limit: LIMIT,
    })
  );

  const histories = getMentorBalanceHistoryQuery.data?.result.data ?? [];
  const totalData = getMentorBalanceHistoryQuery.data?.result.total_data ?? 0;
  const lastPage = getMentorBalanceHistoryQuery.data?.result.last_page ?? 1;

  if (getMentorBalanceHistoryQuery.isPending) {
    return <MentorBalanceHistorySkeleton />;
  }

  if (getMentorBalanceHistoryQuery.isError) {
    return (
      <div className="flex flex-col items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 p-4">
        <p className="text-sm text-destructive">
          Gagal memuat riwayat saldo. Silakan coba lagi.
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => getMentorBalanceHistoryQuery.refetch()}
        >
          Coba Lagi
        </Button>
      </div>
    );
  }

  if (histories.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-border py-8">
        <ReceiptIcon className="size-8 text-muted-foreground" />
        <p className="text-sm font-medium text-foreground">
          Belum ada riwayat saldo
        </p>
        <p className="text-xs text-muted-foreground">
          Mentor ini belum memiliki mutasi saldo.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs text-muted-foreground">
        {totalData} mutasi tercatat
      </p>

      <div className="flex max-h-80 flex-col gap-2 overflow-y-auto pr-1">
        {histories.map(history => (
          <MentorBalanceHistoryItem key={history.id} history={history} />
        ))}
      </div>

      {lastPage > 1 && (
        <div className="flex items-center justify-between gap-2 border-t border-border pt-3">
          <span className="text-xs text-muted-foreground">
            Halaman {page} dari {lastPage}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage(currentPage => currentPage - 1)}
            >
              Sebelumnya
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= lastPage}
              onClick={() => setPage(currentPage => currentPage + 1)}
            >
              Berikutnya
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

interface MentorBalanceHistoryItemProps {
  history: MentorBalanceHistoryEntry;
}

function MentorBalanceHistoryItem({ history }: MentorBalanceHistoryItemProps) {
  const isIncrease = history.change_type === 'INCREASE';

  return (
    <div className="flex items-start gap-3 rounded-lg border border-border bg-background p-3">
      <span
        className={
          isIncrease
            ? 'flex size-8 shrink-0 items-center justify-center rounded-full bg-emerald-500/10 text-emerald-600'
            : 'flex size-8 shrink-0 items-center justify-center rounded-full bg-destructive/10 text-destructive'
        }
      >
        {isIncrease ? (
          <ArrowDownLeftIcon className="size-4" />
        ) : (
          <ArrowUpRightIcon className="size-4" />
        )}
      </span>

      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge variant="secondary">{history.type}</Badge>
          {history.status && <Badge variant="neutral">{history.status}</Badge>}
        </div>
        {history.description && (
          <p className="wrap-break-word text-xs text-foreground">
            {history.description}
          </p>
        )}
        <p className="text-xs text-muted-foreground">
          {formatRupiah(history.balance_before)} →{' '}
          {formatRupiah(history.balance_after)}
        </p>
        {history.held_until && (
          <p className="text-xs text-muted-foreground">
            Ditahan hingga {formatDate(history.held_until)}
          </p>
        )}
        {history.sent_at && (
          <p className="text-xs text-muted-foreground">
            Dikirim {formatDate(history.sent_at)}
          </p>
        )}
      </div>

      <p
        className={
          isIncrease
            ? 'shrink-0 text-sm font-semibold text-emerald-600'
            : 'shrink-0 text-sm font-semibold text-destructive'
        }
      >
        {isIncrease ? '+' : '-'}
        {formatRupiah(history.amount)}
      </p>
    </div>
  );
}

function MentorBalanceHistorySkeleton() {
  return (
    <div className="flex flex-col gap-2">
      {Array.from({ length: 3 }).map((_, index) => (
        <Skeleton key={index} className="h-[72px] w-full rounded-lg" />
      ))}
    </div>
  );
}

function MentorBalanceHistorySection({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
        <HistoryIcon className="size-4" />
        <span>Riwayat Saldo</span>
      </div>
      {children}
    </div>
  );
}
